import { Button } from "@chakra-ui/button";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from "@chakra-ui/form-control";
import { Input } from "@chakra-ui/input";
import { Box, HStack, Link, Stack } from "@chakra-ui/layout";
import { Textarea } from "@chakra-ui/textarea";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { FC, useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import { createReportContent } from "@modules/Layout/helpers";
import {
  useShowErrorToast,
  useShowSuccessToast,
  useSubmitRequestContact,
} from "@modules/Layout/hooks/useSubmitRequestContact";
import { IInformFormInputs } from "@modules/Layout/models/demoForm";
import BotDetectInput from "@modules/Layout/components/BotDetectInput";

const schema = yup.object().shape({
  firstName: yup.string().required("First name is required."),
  lastName: yup.string(),
  email: yup
    .string()
    .email("Please enter a valid email address.")
    .required("Email is required."),
  brokenUrl: yup.string(),
  description: yup.string(),
});

interface Props {}

const InformForm: React.FC<Props> = () => {
  const successToast = useShowSuccessToast();
  const errorToast = useShowErrorToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<IInformFormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { mutate: createSubmitRequestContactMutate } =
    useSubmitRequestContact();

  const onSubmit: SubmitHandler<IInformFormInputs> = useCallback(
    async (data: IInformFormInputs) => {
      const payload = {
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        content: createReportContent(data),
        formType: "contact",
      };

      createSubmitRequestContactMutate(payload, {
        onSuccess: (res: any) => {
          res.body.success
            ? successToast(res.body.message)
            : errorToast(res.body.message);
          reset();
        },
        onError: err => {
          errorToast(
            "We are unable to send your mail. Please try again later."
          );
        },
      });
    },
    [createSubmitRequestContactMutate]
  );

  return (
    <Stack spacing={4} width={{ base: "full", md: "90%", lg: "full" }}>
      <Box as={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormControl isInvalid={!!errors?.firstName?.message} py="4" isRequired>
          <FormLabel>First Name</FormLabel>
          <Input {...register("firstName")} borderColor={"grey300"} />
          <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl py="4">
          <FormLabel>Last Name</FormLabel>
          <Input {...register("lastName")} borderColor={"grey300"} />
          <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.email?.message} py="4" isRequired>
          <FormLabel>Email</FormLabel>
          <Input type="email" {...register("email")} borderColor={"grey300"} />
          <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
        </FormControl>

        <FormControl py="4">
          <FormLabel>Broken URL</FormLabel>
          <Input {...register("brokenUrl")} borderColor={"grey300"} />
        </FormControl>

        <FormControl py="4">
          <FormLabel>Description</FormLabel>
          <Textarea
            {...register("description")}
            placeholder="Please describe any  information that will help us to fix the link."
            borderColor={"grey300"}
          />
        </FormControl>

        <FormControl py="4">
          <HStack gap={10}>
            <Button
              variant={"primary"}
              px={"40px"}
              type="submit"
              isLoading={isSubmitting}
              loadingText="Submitting"
            >
              Submit
            </Button>
            <Link href="/privacy-policy">Privacy Policy</Link>
          </HStack>
        </FormControl>

        <BotDetectInput />
      </Box>
    </Stack>
  );
};

export default InformForm;
